import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLearnerAuth } from "@/contexts/LearnerAuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LearnerLogin() {
  const { learner, loading, login } = useLearnerAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [appName, setAppName] = useState("DiNAXLP");

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => {
      if (data.app_name) setAppName(data.app_name);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && learner) {
      navigate("/portal/dashboard", { replace: true });
    }
  }, [learner, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex items-center justify-center">
        <div
          className="h-8 w-8 border-2 border-gray-200 rounded-full animate-spin"
          style={{ borderTopColor: "var(--primary-color, #4A90D9)" }}
        />
      </div>
    );
  }

  if (learner) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email);
      navigate("/portal/dashboard", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Unable to sign in. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F8F9FC] via-white to-[#EEF1F8] relative overflow-hidden"
      data-testid="learner-login-page"
    >
      {/* Soft decorative circles */}
      <div className="absolute top-[-120px] right-[-80px] w-[400px] h-[400px] rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, var(--primary-color, #4A90D9) 0%, transparent 70%)" }}
      />
      <div className="absolute bottom-[-100px] left-[-60px] w-[300px] h-[300px] rounded-full opacity-15"
        style={{ background: "radial-gradient(circle, var(--primary-color, #4A90D9) 0%, transparent 70%)" }}
      />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-xl shadow-gray-200/50">
          <div className="text-center mb-8">
            <h1
              className="font-heading text-3xl font-bold text-gray-900 tracking-tight"
              data-testid="learner-login-logo"
            >
              DiNA
              <span style={{ color: "var(--primary-color, #4A90D9)" }}>XLP</span>
            </h1>
            <p className="text-gray-500 text-sm mt-2 font-body">
              Learner Portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="learner-email" className="text-gray-600 font-body text-sm">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="learner-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  data-testid="learner-email-input"
                  className="pl-10 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[var(--primary-color,#4A90D9)] focus:ring-1 focus:ring-[var(--primary-color,#4A90D9)] h-11"
                />
              </div>
              <p className="text-xs text-gray-400 font-body">
                Use the email address provided by your program administrator.
              </p>
            </div>

            {error && (
              <div
                className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
                data-testid="learner-login-error"
              >
                <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                <p className="text-red-600 text-sm font-body">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="learner-login-submit"
              className="w-full h-11 font-body font-medium text-white rounded-xl transition-all duration-200 hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {submitting ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400 font-body">
              Admin?{" "}
              <a href="/admin" className="hover:underline" style={{ color: "var(--primary-color, #4A90D9)" }}>
                Go to Admin Portal
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
